import { z } from 'zod';

// Email validation regex - matches standard email format
const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

// Phone validation - allows digits, spaces, dashes, parentheses, and plus sign
const phoneRegex = /^[0-9\-+().\s]{10,25}$/;

// Lead form validation schema
export const leadSchema = z.object({
  first_name: z.string()
    .min(1, 'First name is required')
    .max(100, 'First name must be less than 100 characters')
    .transform(val => val.trim()),
  last_name: z.string()
    .min(1, 'Last name is required')
    .max(100, 'Last name must be less than 100 characters')
    .transform(val => val.trim()),
  email: z.string()
    .min(1, 'Email is required')
    .max(255, 'Email must be less than 255 characters')
    .regex(emailRegex, 'Please enter a valid email address')
    .transform(val => val.toLowerCase().trim()),
  phone: z.string()
    .min(1, 'Phone is required')
    .regex(phoneRegex, 'Please enter a valid phone number')
    .transform(val => val.trim()),
  company: z.string()
    .min(1, 'Company is required')
    .max(200, 'Company name must be less than 200 characters')
    .transform(val => val.trim()),
  job_title: z.string()
    .max(100, 'Job title must be less than 100 characters')
    .optional()
    .transform(val => val?.trim() || ''),
  service_interest: z.string()
    .max(100, 'Service selection must be less than 100 characters')
    .optional()
    .default(''),
  industry: z.string()
    .max(100, 'Industry must be less than 100 characters')
    .optional()
    .default(''),
  project_type: z.string()
    .max(100, 'Project type must be less than 100 characters')
    .optional()
    .default(''),
  project_timeline: z.string()
    .max(100, 'Timeline must be less than 100 characters')
    .optional()
    .default(''),
  estimated_budget: z.string()
    .max(100, 'Budget must be less than 100 characters')
    .optional()
    .default(''),
  facility_size: z.string()
    .max(100, 'Facility size must be less than 100 characters')
    .optional()
    .default(''),
  facility_location_city: z.string()
    .max(100, 'City must be less than 100 characters')
    .optional()
    .transform(val => val?.trim() || ''),
  facility_location_state: z.string()
    .max(100, 'State/Province must be less than 100 characters')
    .optional()
    .transform(val => val?.trim() || ''),
  facility_location_country: z.string()
    .max(100, 'Country must be less than 100 characters')
    .optional()
    .default('Canada'),
  current_equipment: z.string()
    .max(500, 'Current equipment must be less than 500 characters')
    .optional()
    .transform(val => val?.trim() || ''),
  project_description: z.string()
    .max(2000, 'Project description must be less than 2000 characters')
    .optional()
    .transform(val => val?.trim() || ''),
  how_heard_about_us: z.string()
    .max(100, 'How heard must be less than 100 characters')
    .optional()
    .default(''),
  preferred_contact_method: z.string()
    .max(50, 'Contact method must be less than 50 characters')
    .optional()
    .default('email'),
  best_time_to_call: z.string()
    .max(100, 'Best time must be less than 100 characters')
    .optional()
    .default(''),
  current_step: z.number().optional().default(1),
  source_page: z.string().max(255).optional().default(''),
});

// Newsletter signup validation schema
export const newsletterSchema = z.object({
  email: z.string()
    .min(1, 'Email is required')
    .max(255, 'Email must be less than 255 characters')
    .regex(emailRegex, 'Please enter a valid email address')
    .transform(val => val.toLowerCase().trim()),
  first_name: z.string()
    .max(100, 'First name must be less than 100 characters')
    .optional()
    .transform(val => val?.trim() || null),
  source_page: z.string().max(255).optional(),
  utm_source: z.string().max(100).optional().nullable(),
  utm_medium: z.string().max(100).optional().nullable(),
  utm_campaign: z.string().max(100).optional().nullable(),
});

// Resource download form validation schema
export const resourceDownloadSchema = z.object({
  email: z.string()
    .min(1, 'Email is required')
    .max(255, 'Email must be less than 255 characters')
    .regex(emailRegex, 'Please enter a valid email address')
    .transform(val => val.toLowerCase().trim()),
  first_name: z.string()
    .max(100, 'First name must be less than 100 characters')
    .optional()
    .transform(val => val?.trim() || null),
  company: z.string()
    .max(200, 'Company name must be less than 200 characters')
    .optional()
    .transform(val => val?.trim() || null),
  resource_slug: z.string()
    .min(1)
    .max(255),
  resource_title: z.string()
    .min(1)
    .max(255),
  newsletter_opt_in: z.boolean().optional().default(false),
  source_page: z.string().max(255).optional(),
  utm_source: z.string().max(100).optional().nullable(),
  utm_medium: z.string().max(100).optional().nullable(),
  utm_campaign: z.string().max(100).optional().nullable(),
});

// Analytics event validation schema
export const analyticsEventSchema = z.object({
  session_id: z.string()
    .min(1)
    .max(100),
  event_type: z.string()
    .min(1)
    .max(100),
  page_path: z.string()
    .max(500),
  page_title: z.string()
    .max(255)
    .optional()
    .nullable(),
  referrer: z.string()
    .max(500)
    .optional()
    .nullable(),
  utm_source: z.string().max(100).optional().nullable(),
  utm_medium: z.string().max(100).optional().nullable(),
  utm_campaign: z.string().max(100).optional().nullable(),
  event_data: z.record(z.unknown()).optional().default({}),
  user_agent: z.string().max(500).optional().nullable(),
  screen_width: z.number().int().min(0).max(10000).optional().nullable(),
  screen_height: z.number().int().min(0).max(10000).optional().nullable(),
});

// Cookie consent validation schema
export const cookieConsentSchema = z.object({
  session_id: z.string()
    .min(1)
    .max(100),
  analytics_consent: z.boolean().default(false),
  functional_consent: z.boolean().default(true),
  marketing_consent: z.boolean().default(false),
  user_agent: z.string().max(500).optional().nullable(),
  ip_country: z.string().max(10).optional().nullable(),
});

// Ebook/lead capture form validation (simplified)
export const ebookLeadSchema = z.object({
  first_name: z.string()
    .min(1, 'First name is required')
    .max(100, 'First name must be less than 100 characters')
    .transform(val => val.trim()),
  email: z.string()
    .min(1, 'Email is required')
    .max(255, 'Email must be less than 255 characters')
    .regex(emailRegex, 'Please enter a valid email address')
    .transform(val => val.toLowerCase().trim()),
  company: z.string()
    .max(200, 'Company name must be less than 200 characters')
    .optional()
    .transform(val => val?.trim() || ''),
  service_interest: z.string()
    .max(100)
    .optional()
    .default(''),
  source_page: z.string().max(255).optional().default(''),
});

// Type exports
export type LeadFormData = z.infer<typeof leadSchema>;
export type NewsletterFormData = z.infer<typeof newsletterSchema>;
export type ResourceDownloadFormData = z.infer<typeof resourceDownloadSchema>;
export type AnalyticsEventData = z.infer<typeof analyticsEventSchema>;
export type CookieConsentData = z.infer<typeof cookieConsentSchema>;
export type EbookLeadFormData = z.infer<typeof ebookLeadSchema>;

// Validation helper that returns errors in a user-friendly format
export function validateForm<T>(
  schema: z.ZodSchema<T>,
  data: unknown
): { success: true; data: T } | { success: false; errors: Record<string, string> } {
  const result = schema.safeParse(data);
  
  if (result.success) {
    return { success: true, data: result.data };
  }
  
  const errors: Record<string, string> = {};
  result.error.errors.forEach((err) => {
    const path = err.path.join('.');
    if (!errors[path]) {
      errors[path] = err.message;
    }
  });
  
  return { success: false, errors };
}
